import { motion } from 'framer-motion';

const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return '0m';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

/**
 * Session-phase welcome view shown to participants.
 * Identical visual content as the lobby's "Session" phase welcome, extracted so the
 * in-session page can render the same block when the user clicks "Welcome" in the sidebar.
 *
 * Props:
 * - session: session object (required — used for name, code, status, subSessions, duration, organization)
 * - participantCount: number — live participant count for the stats card
 * - workbookViewsCount: number — how many workbooks the participant has opened (for Content Viewed %)
 * - respondedActivitiesCount: number — how many launched activities the participant has responded to
 * - onContent, onWorkbooks, onActivities, onWorkspace: click handlers for the Quick Actions grid
 */
const SessionPhaseWelcome = ({
  session,
  participantCount = 0,
  workbookViewsCount = 0,
  respondedActivitiesCount = 0,
  onContent,
  onWorkbooks,
  onActivities,
  onWorkspace,
}) => {
  if (!session) return null;

  // Derive metrics the same way the lobby does (so numbers match exactly).
  const totalActivities = session.subSessions?.reduce((sum, ss) => sum + (ss.activities?.length || 0), 0) || 0;
  const launchedActivities = session.subSessions?.reduce((sum, ss) =>
    sum + (ss.activities?.filter(a =>
      a.status === 'active' || a.status === 'completed' || a.status === 'closed' ||
      a.launchedAt || a.isLaunched || (a.responses && a.responses.length > 0)
    ).length || 0), 0) || 0;

  const respondedActivities = respondedActivitiesCount ?? (session.userResponses?.length || 0);
  const activitiesCompletedPercent = launchedActivities > 0
    ? Math.round((respondedActivities / launchedActivities) * 100)
    : 0;

  const quizActivities = session.subSessions?.flatMap(ss =>
    ss.activities?.filter(a => a.type?.toLowerCase() === 'quiz') || []
  ) || [];
  const totalQuizzes = quizActivities.length;
  const quizzesPassed = (session.userResponses || []).filter(r => {
    if (r.activityType?.toLowerCase() !== 'quiz') return false;
    const score = r.response?.results?.percentage || r.response?.score || r.score || 0;
    return score >= 70;
  }).length;
  const quizzesPassedPercent = totalQuizzes > 0 ? Math.round((quizzesPassed / totalQuizzes) * 100) : 0;

  const totalWorkbooks = session.workbooks?.length || 0;
  const contentViewedPercent = totalWorkbooks > 0
    ? Math.round((workbookViewsCount / totalWorkbooks) * 100)
    : 0;

  const engagementScore = activitiesCompletedPercent;
  const scoreLabel = engagementScore >= 80 ? 'Excellent'
    : engagementScore >= 60 ? 'Good'
    : engagementScore >= 40 ? 'Average'
    : engagementScore > 0 ? 'Low'
    : 'N/A';
  const scoreColor = engagementScore >= 60 ? 'text-green-600'
    : engagementScore >= 40 ? 'text-yellow-600'
    : 'text-neutral-400';

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning!';
    if (hour < 17) return 'Good Afternoon!';
    return 'Good Evening!';
  })();

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      {/* Greeting Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">{greeting}</h1>
          <p className="text-sm sm:text-base text-neutral-500 mt-1">
            Welcome to <span className="font-medium text-neutral-700">{session.name}</span>
          </p>
        </div>
        <div className="sm:text-right flex sm:flex-col items-center sm:items-end gap-2 sm:gap-0">
          <p className="text-xs sm:text-sm text-neutral-500 hidden sm:block">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
          <div className="inline-flex items-center gap-2 sm:mt-1 px-3 py-1.5 bg-neutral-100 rounded-full">
            <span className="text-xs text-neutral-500">Code:</span>
            <span className="text-xs font-mono font-semibold text-neutral-900">#{session.code}</span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6">
        <div className="bg-white rounded-xl border border-neutral-200 p-3 sm:p-4 text-center hover:shadow-sm transition-shadow">
          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-green-50 rounded-lg flex items-center justify-center mx-auto mb-1.5 sm:mb-2">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <p className="text-[10px] text-neutral-500 uppercase tracking-wide mb-0.5">Participants</p>
          <p className="text-2xl font-bold text-neutral-900">{participantCount}</p>
          <p className="text-xs text-green-600 flex items-center justify-center gap-1">
            <span className="w-1 h-1 bg-green-500 rounded-full animate-pulse"></span>
            Active now
          </p>
        </div>

        {session.duration > 0 && (
          <div className="bg-white rounded-xl border border-neutral-200 p-3 sm:p-4 text-center hover:shadow-sm transition-shadow">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-1.5 sm:mb-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-[10px] text-neutral-500 uppercase tracking-wide mb-0.5">Duration</p>
            <p className="text-xl sm:text-2xl font-bold text-neutral-900">{formatDuration(session.duration)}</p>
          </div>
        )}

        <div className="bg-white rounded-xl border border-neutral-200 p-3 sm:p-4 text-center hover:shadow-sm transition-shadow">
          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-purple-50 rounded-lg flex items-center justify-center mx-auto mb-1.5 sm:mb-2">
            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <p className="text-[10px] text-neutral-500 uppercase tracking-wide mb-0.5">Organization</p>
          <p className="text-base sm:text-lg font-bold text-neutral-900 truncate">{session.organization?.name || 'N/A'}</p>
          <p className="text-xs text-neutral-500">Host</p>
        </div>
      </div>

      {/* Progress / Status / Engagement Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {/* Session Progress */}
        <div className="bg-white rounded-xl sm:rounded-2xl border border-neutral-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg font-semibold text-neutral-900">Session Progress</h3>
            <span className="text-xs sm:text-sm text-neutral-500">Today</span>
          </div>
          <div className="space-y-3 sm:space-y-4">
            {/* Content Viewed — only rendered if this session has any workbooks/content,
                otherwise "viewed 0 of 0" is always 0% and confuses the participant. */}
            {totalWorkbooks > 0 && (
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs sm:text-sm font-medium text-neutral-900">Content Viewed</span>
                    <span className="text-xs sm:text-sm text-neutral-500">{contentViewedPercent}%</span>
                  </div>
                  <div className="h-1.5 sm:h-2 bg-neutral-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${contentViewedPercent}%` }}></div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs sm:text-sm font-medium text-neutral-900">Activities Completed</span>
                  <span className="text-xs sm:text-sm text-neutral-500">{activitiesCompletedPercent}%</span>
                </div>
                <div className="h-1.5 sm:h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div className="h-full bg-orange-500 rounded-full transition-all duration-500" style={{ width: `${activitiesCompletedPercent}%` }}></div>
                </div>
              </div>
            </div>

            {/* Quizzes Passed — only rendered if this session has any quiz activities. */}
            {totalQuizzes > 0 && (
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs sm:text-sm font-medium text-neutral-900">Quizzes Passed</span>
                    <span className="text-xs sm:text-sm text-neutral-500">{quizzesPassedPercent}%</span>
                  </div>
                  <div className="h-1.5 sm:h-2 bg-neutral-100 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full transition-all duration-500" style={{ width: `${quizzesPassedPercent}%` }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Session Status Ring */}
        <div className="bg-white rounded-xl sm:rounded-2xl border border-neutral-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg font-semibold text-neutral-900">Session Status</h3>
            <span className={`flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full ${
              session.status === 'live' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${session.status === 'live' ? 'bg-green-500 animate-pulse' : 'bg-blue-500'}`}></span>
              {session.status === 'live' ? 'Live' : session.status === 'scheduled' ? 'Scheduled' : session.status}
            </span>
          </div>
          <div className="flex items-center justify-center py-2 sm:py-4">
            <div className="relative w-28 h-28 sm:w-32 sm:h-32">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 160 160">
                <circle cx="80" cy="80" r="70" fill="none" stroke="#f3f4f6" strokeWidth="12" />
                <circle cx="80" cy="80" r="70" fill="none" stroke="#3b82f6" strokeWidth="12" strokeLinecap="round"
                  strokeDasharray={`${activitiesCompletedPercent * 4.4} 440`} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl sm:text-3xl font-bold text-neutral-900">{respondedActivities}</span>
                <span className="text-[10px] sm:text-xs text-neutral-500 uppercase tracking-wide">of {launchedActivities || totalActivities}</span>
                <span className="text-[10px] sm:text-xs text-neutral-400">Launched</span>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-4 sm:gap-6 mt-2 sm:mt-4">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-blue-500 rounded-full"></span>
              <span className="text-xs sm:text-sm text-neutral-600">Completed</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-neutral-200 rounded-full"></span>
              <span className="text-xs sm:text-sm text-neutral-600">Pending</span>
            </div>
          </div>
        </div>

        {/* Engagement Score Ring */}
        <div className="bg-white rounded-xl sm:rounded-2xl border border-neutral-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg font-semibold text-neutral-900">Engagement Score</h3>
          </div>
          <div className="flex items-center justify-center py-2 sm:py-4">
            <div className="relative w-28 h-28 sm:w-32 sm:h-32">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 160 160">
                <circle cx="80" cy="80" r="70" fill="none" stroke="#f3f4f6" strokeWidth="12" />
                <circle cx="80" cy="80" r="70" fill="none" stroke="#ff8a00" strokeWidth="12" strokeLinecap="round"
                  strokeDasharray={`${engagementScore * 4.4} 440`} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl sm:text-3xl font-bold text-neutral-900">{engagementScore}</span>
                <span className="text-[10px] sm:text-xs text-neutral-500 uppercase tracking-wide">Score</span>
                <span className={`text-[10px] sm:text-xs font-medium ${scoreColor}`}>{scoreLabel}</span>
              </div>
            </div>
          </div>
          <div className="space-y-2 mt-2 sm:mt-4">
            <div className="flex items-center justify-between text-xs sm:text-sm">
              <span className="text-neutral-500 flex items-center gap-1.5">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Activities Responded
              </span>
              <span className="font-medium text-neutral-900">{respondedActivities}/{launchedActivities || totalActivities}</span>
            </div>
            <div className="flex items-center justify-between text-xs sm:text-sm">
              <span className="text-neutral-500 flex items-center gap-1.5">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                Response Rate
              </span>
              <span className="font-medium text-neutral-900">{activitiesCompletedPercent}%</span>
            </div>
            <div className="flex items-center justify-between text-xs sm:text-sm">
              <span className="text-neutral-500 flex items-center gap-1.5">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                Session Status
              </span>
              <span className="font-medium text-neutral-900 capitalize">{session.status}</span>
            </div>
          </div>
        </div>
      </div>

    </motion.div>
  );
};

export default SessionPhaseWelcome;
